import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
    FaArrowLeft, FaMagic, FaPlus, FaCheck, FaTrash, FaImage, FaPen, FaFolderOpen,
    FaTimes, FaLayerGroup, FaChevronRight,
} from "react-icons/fa";
import { authFetch } from "../../utils/api/authFetch";
import { useTeacherTaxonomy } from "../../hooks/useTeacherTaxonomy";
import { useConfirm } from "../../components/useConfirm";
import { MathText, MathJaxContext, mathJaxConfig } from "../../components/MathText";
import { SubjectPicker, ClassPills, TopicSelect, SubjectIcon, ProfileSetupNotice } from "../../components/marketplace";
import { QuestionEditor } from "../../components/QuestionForm";

// The teacher's question bank: write one by hand, generate a batch with AI, or
// browse what you already have.
//
// Two things drive the layout. Subjects, classes and topics come from the
// teacher's own profile (useTeacherTaxonomy) rather than free-text boxes — a
// question typed under "Bio" instead of "Biology" is invisible to every student
// filter. And because generation runs per sub-topic, the bank fills up as a mix
// across several lessons, so "My Questions" is filterable down to a single
// sub-topic instead of being one flat list.

const EMPTY_FORM = {
    subject: "", class: "", topic: "", level: "Medium",
    question: "", option_a: "", option_b: "", option_c: "", option_d: "",
    correctAnswer: "A", explanation: "", question_type: "mcq",
};

const QTYPE_LABELS = { mcq: "Multiple choice", theory: "Theory" };

// The AI can only work from text: typed notes, or a PDF/Word/PowerPoint file it
// can read. Videos, images and scanned pages have nothing to extract, so those
// sub-topics are shown disabled rather than failing mid-run.
const AI_READABLE_KINDS = new Set(["pdf", "docx", "office"]);
const isAiReadable = (seg) => !!seg.hasText || (seg.hasAttachment && AI_READABLE_KINDS.has(seg.attachment_kind));

const TABS = [
    { id: "manual", label: "Write a question", Icon: FaPen },
    { id: "ai", label: "Generate with AI", Icon: FaMagic },
    { id: "mine", label: "My questions", Icon: FaFolderOpen },
];

const TeacherQuestionBuilder = () => {
    const navigate = useNavigate();
    const [tab, setTab] = useState("manual");
    const { confirm, ConfirmDialog } = useConfirm();

    // Subjects/classes are the teacher's own; topics are global-per-subject.
    const { subjects, classes, topicsBySubject, hasSubjects, hasClasses, needsSetup } = useTeacherTaxonomy();

    const [form, setForm] = useState(EMPTY_FORM);
    const [image, setImage] = useState(null);
    const [saving, setSaving] = useState(false);

    // ---- AI tab: subject -> lesson -> its sub-topics ----
    const [myLessons, setMyLessons] = useState([]);
    const [aiSubject, setAiSubject] = useState("");
    const [aiLessonId, setAiLessonId] = useState("");
    const [aiSegments, setAiSegments] = useState([]);
    const [aiSelected, setAiSelected] = useState(() => new Set());
    const [loadingSegments, setLoadingSegments] = useState(false);
    const [aiCounts, setAiCounts] = useState({ mcqCount: 5, theoryCount: 2, flashcardCount: 0 });
    const [generating, setGenerating] = useState(false);

    // ---- My questions: the filter set ----
    const [questions, setQuestions] = useState([]);
    const [facets, setFacets] = useState({ subjects: [], topicsBySubject: {}, lessons: [], counts: {} });
    const [mineFilters, setMineFilters] = useState({
        review_status: "draft", subject: "", topic: "", lesson_id: "", segment_id: "",
    });
    const [mineSegments, setMineSegments] = useState([]);
    const [loadingMine, setLoadingMine] = useState(false);
    // The question currently open in the inline editor.
    const [editingQId, setEditingQId] = useState(null);

    const fetchMine = async () => {
        setLoadingMine(true);
        try {
            const params = new URLSearchParams();
            Object.entries(mineFilters).forEach(([k, v]) => { if (v) params.append(k, v); });
            const res = await authFetch(`/api/teacher-questions/mine?${params.toString()}`);
            const data = await res.json();
            setQuestions(data.data || []);
            if (data.facets) setFacets(data.facets);
        } catch {
            toast.error("Could not load your questions");
        } finally {
            setLoadingMine(false);
        }
    };

    useEffect(() => { if (tab === "mine") fetchMine(); }, [tab, mineFilters]);

    // The lesson list feeds both the AI picker and the "My questions" filter.
    useEffect(() => {
        if (myLessons.length) return;
        (async () => {
            try {
                const res = await authFetch("/api/lessons/mine");
                const data = await res.json();
                setMyLessons(data.data || []);
            } catch {
                /* the pickers just stay empty; no need to shout on page load */
            }
        })();
    }, []);

    // Sub-topics for the lesson chosen in the AI tab, with every readable one
    // pre-ticked.
    useEffect(() => {
        if (!aiLessonId) { setAiSegments([]); setAiSelected(new Set()); return; }
        (async () => {
            setLoadingSegments(true);
            try {
                const res = await authFetch(`/api/teacher-questions/lessons/${aiLessonId}/sub-topics`);
                const data = await res.json();
                const segs = data.data?.segments || [];
                setAiSegments(segs);
                setAiSelected(new Set(segs.filter(isAiReadable).map((s) => s.id)));
            } catch {
                toast.error("Could not load that lesson's sub-topics");
            } finally {
                setLoadingSegments(false);
            }
        })();
    }, [aiLessonId]);

    // Sub-topics for the lesson chosen in the "My questions" filter bar.
    useEffect(() => {
        if (!mineFilters.lesson_id) { setMineSegments([]); return; }
        (async () => {
            try {
                const res = await authFetch(`/api/teacher-questions/lessons/${mineFilters.lesson_id}/sub-topics`);
                const data = await res.json();
                setMineSegments(data.data?.segments || []);
            } catch {
                setMineSegments([]);
            }
        })();
    }, [mineFilters.lesson_id]);

    const toggleSegment = (sid) => setAiSelected((prev) => {
        const next = new Set(prev);
        if (next.has(sid)) next.delete(sid); else next.add(sid);
        return next;
    });

    const handleManualSubmit = async (e) => {
        e.preventDefault();
        if (!form.subject) { toast.error("Choose a subject"); return; }
        if (!form.class) { toast.error("Choose a class"); return; }
        if (!form.topic.trim()) { toast.error("Choose a topic"); return; }
        if (!form.question.trim()) { toast.error("Write the question text"); return; }
        if (form.question_type === "mcq" && !["option_a", "option_b", "option_c", "option_d"].every((k) => form[k].trim())) {
            toast.error("A multiple-choice question needs all four options");
            return;
        }

        setSaving(true);
        try {
            const body = new FormData();
            Object.entries(form).forEach(([key, value]) => body.append(key, value));
            if (image) body.append("question_image", image);

            const res = await authFetch("/api/teacher-questions", { method: "POST", body });
            const data = await res.json();
            if (!res.ok) throw new Error(data.message || "Failed to create question");
            toast.success("Question created");
            // Keep subject/class/topic so a teacher can write several in a row.
            setForm((f) => ({ ...EMPTY_FORM, subject: f.subject, class: f.class, topic: f.topic, level: f.level }));
            setImage(null);
        } catch (err) {
            toast.error(err.message);
        } finally {
            setSaving(false);
        }
    };

    const handleGenerate = async (e) => {
        // Called both as a form submit handler and, on retry, with no event.
        e?.preventDefault?.();
        if (!aiLessonId) { toast.error("Choose a lesson first"); return; }
        if (!aiSelected.size) { toast.error("Pick at least one sub-topic"); return; }

        setGenerating(true);
        try {
            const res = await authFetch("/api/teacher-questions/generate", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ lessonId: aiLessonId, segmentIds: [...aiSelected], ...aiCounts }),
            });
            const data = await res.json();
            if (!res.ok) {
                // Already phrased for a teacher by the API ("The AI is busy right
                // now…"); `retryable` says whether trying again is worth offering.
                const err = new Error(data.message || "The AI couldn't be reached just now. Please try again in a moment.");
                err.retryable = data.retryable !== false;
                throw err;
            }

            toast.success(data.message || "Draft questions generated");
            // The API reports each sub-topic it couldn't read — surface it rather
            // than leaving the teacher to wonder why one produced nothing.
            (data.data?.skipped || []).forEach((s) =>
                toast.warning(`Skipped "${s.title}" — ${s.reason}`, { duration: 7000 })
            );
            (data.data?.notes || []).forEach((n) =>
                toast.info(`"${n.title}" — ${n.reason}`, { duration: 8000 })
            );
            // Drop them straight into the drafts for this lesson.
            setMineFilters({ review_status: "draft", subject: "", topic: "", lesson_id: String(aiLessonId), segment_id: "" });
            setTab("mine");
        } catch (err) {
            toast.error(err.message, {
                duration: 8000,
                ...(err.retryable ? { action: { label: "Try again", onClick: () => handleGenerate() } } : {}),
            });
        } finally {
            setGenerating(false);
        }
    };

    const approveQuestion = async (id) => {
        try {
            const res = await authFetch(`/api/teacher-questions/${id}/approve`, { method: "PATCH" });
            if (!res.ok) throw new Error("Failed to approve");
            toast.success("Question approved — now visible to students");
            fetchMine();
        } catch (err) {
            toast.error(err.message);
        }
    };

    // Fix a question in place — an AI draft with a wrong option, or an approved
    // one that needs rewording — instead of deleting it and starting again.
    const saveQuestion = async (id, form) => {
        const res = await authFetch(`/api/teacher-questions/${id}`, {
            method: "PATCH",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(form),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.message || "Couldn't save the question");
        toast.success("Question updated");
        setEditingQId(null);
        fetchMine();
    };

    const deleteQuestion = async (id) => {
        const ok = await confirm({
            title: "Delete this question?",
            message: "It will be removed from your question bank for good.",
            confirmLabel: "Delete",
            danger: true,
        });
        if (!ok) return;
        try {
            const res = await authFetch(`/api/teacher-questions/${id}`, { method: "DELETE" });
            if (!res.ok) throw new Error("Failed to delete");
            toast.success("Deleted");
            fetchMine();
        } catch (err) {
            toast.error(err.message);
        }
    };

    // Lessons narrowed by the subject chosen in the AI tab.
    const aiLessonOptions = useMemo(
        () => (aiSubject ? myLessons.filter((l) => l.subject === aiSubject) : myLessons),
        [myLessons, aiSubject]
    );

    const setMine = (patch) => setMineFilters((f) => {
        const next = { ...f, ...patch };
        // Changing a broader filter invalidates the narrower ones below it.
        if (patch.subject !== undefined) { next.topic = ""; next.lesson_id = ""; next.segment_id = ""; }
        if (patch.lesson_id !== undefined) next.segment_id = "";
        return next;
    });

    const activeFilterCount = ["subject", "topic", "lesson_id", "segment_id"].filter((k) => mineFilters[k]).length;

    return (
        <MathJaxContext config={mathJaxConfig}>
            <div className="min-h-screen bg-gray-50 font-montserrat">
                {ConfirmDialog}

                <div className="sticky top-0 z-40 shadow-md">
                    <nav className="bg-[#2148C0] px-4 sm:px-6 py-3 flex items-center gap-3">
                        <button onClick={() => navigate("/teacher-dashboard")} className="text-white shrink-0">
                            <FaArrowLeft size={18} />
                        </button>
                        <h1 className="text-lg sm:text-xl font-bold text-white">Question bank</h1>
                    </nav>
                    <div className="bg-white border-b border-gray-200 px-2 sm:px-6 flex gap-1 overflow-x-auto">
                        {TABS.map(({ id, label, Icon }) => {
                            const active = tab === id;
                            return (
                                <button key={id} onClick={() => setTab(id)}
                                    className={`px-3 sm:px-4 py-3 text-sm font-semibold whitespace-nowrap border-b-2 transition flex items-center gap-2 ${
                                        active ? "border-blue-600 text-blue-700" : "border-transparent text-gray-500 hover:text-gray-700"
                                    }`}>
                                    <Icon size={13} /> {label}
                                </button>
                            );
                        })}
                    </div>
                </div>

                <div className="max-w-4xl mx-auto p-4 sm:p-6">
                    {needsSetup && tab !== "mine" && (
                        <div className="mb-5">
                            <ProfileSetupNotice
                                missingSubjects={!hasSubjects}
                                missingClasses={!hasClasses}
                                onSetup={() => navigate("/teacher/marketplace/profile")}
                            />
                        </div>
                    )}

                    {/* ---------------- Write a question ---------------- */}
                    {tab === "manual" && (
                        <form onSubmit={handleManualSubmit} className="bg-white rounded-2xl shadow-sm p-5 sm:p-6 space-y-5">
                            <p className="text-sm text-gray-500">
                                Tagged with your own subjects, classes and topics so students find it under the right filter.
                            </p>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <SubjectPicker label="Subject" required allowCustom={false} subjects={subjects}
                                    value={form.subject} onChange={(v) => setForm({ ...form, subject: v, topic: "" })} />
                                <ClassPills label="Class" required classes={classes} value={form.class}
                                    onChange={(v) => setForm({ ...form, class: v })}
                                    hint="Only the classes you teach are listed." />
                            </div>

                            <TopicSelect label="Topic" required subject={form.subject} topicsBySubject={topicsBySubject}
                                value={form.topic} onChange={(v) => setForm({ ...form, topic: v })} />

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <label className="text-sm font-medium text-gray-700">Type
                                    <select className="border border-gray-200 rounded-xl p-2.5 w-full mt-1 text-sm" value={form.question_type}
                                        onChange={(e) => setForm({ ...form, question_type: e.target.value })}>
                                        <option value="mcq">Multiple choice</option>
                                        <option value="theory">Theory</option>
                                    </select>
                                </label>
                                <label className="text-sm font-medium text-gray-700">Difficulty
                                    <select className="border border-gray-200 rounded-xl p-2.5 w-full mt-1 text-sm" value={form.level}
                                        onChange={(e) => setForm({ ...form, level: e.target.value })}>
                                        <option>Easy</option><option>Medium</option><option>Hard</option>
                                    </select>
                                </label>
                            </div>

                            <textarea required rows={3}
                                placeholder="Write the question in plain text (avoid $ or LaTeX — e.g. x^2 not $x^2$)"
                                className="border border-gray-200 rounded-xl p-2.5 w-full text-sm focus:border-blue-400 focus:ring-2 focus:ring-blue-100 outline-none"
                                value={form.question} onChange={(e) => setForm({ ...form, question: e.target.value })} />

                            <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer w-fit border border-gray-200 rounded-lg px-3 py-2 hover:border-blue-300">
                                <FaImage size={12} /> <span className="text-xs truncate max-w-[14rem]">{image ? image.name : "Attach an image (optional)"}</span>
                                <input type="file" accept="image/*" className="hidden" onChange={(e) => setImage(e.target.files[0])} />
                            </label>

                            {form.question_type === "mcq" && (
                                <>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                        {["A", "B", "C", "D"].map((letter) => (
                                            <input key={letter} placeholder={`Option ${letter}`}
                                                className="border border-gray-200 rounded-lg p-2.5 text-sm"
                                                value={form[`option_${letter.toLowerCase()}`]}
                                                onChange={(e) => setForm({ ...form, [`option_${letter.toLowerCase()}`]: e.target.value })} />
                                        ))}
                                    </div>
                                    <label className="text-sm font-medium text-gray-700 block">Correct answer
                                        <select className="border border-gray-200 rounded-xl p-2.5 mt-1 w-full sm:w-40 text-sm" value={form.correctAnswer}
                                            onChange={(e) => setForm({ ...form, correctAnswer: e.target.value })}>
                                            {["A", "B", "C", "D"].map((l) => <option key={l}>{l}</option>)}
                                        </select>
                                    </label>
                                </>
                            )}

                            <textarea rows={2} placeholder="Explanation / model answer (optional)"
                                className="border border-gray-200 rounded-xl p-2.5 w-full text-sm focus:border-blue-400 focus:ring-2 focus:ring-blue-100 outline-none"
                                value={form.explanation} onChange={(e) => setForm({ ...form, explanation: e.target.value })} />

                            <button disabled={saving || needsSetup}
                                className="bg-blue-600 hover:bg-blue-700 text-white px-5 py-2.5 rounded-xl font-semibold flex items-center gap-2 disabled:opacity-50">
                                <FaPlus size={12} /> {saving ? "Saving…" : "Add question"}
                            </button>
                        </form>
                    )}

                    {/* ---------------- Generate with AI ---------------- */}
                    {tab === "ai" && (
                        <form onSubmit={handleGenerate} className="bg-white rounded-2xl shadow-sm p-5 sm:p-6 space-y-5">
                            <p className="text-sm text-gray-500">
                                Pick a lesson and the AI writes a separate batch{" "}
                                <span className="font-semibold text-gray-700">for each of its sub-topics</span> — from your
                                typed notes and the text inside any PDF or Word file you uploaded. Everything lands as a
                                draft for you to review.
                            </p>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <label className="text-sm font-medium text-gray-700">
                                    Subject <span className="text-gray-400 font-normal">(narrows the list)</span>
                                    <select className="border border-gray-200 rounded-xl p-2.5 w-full mt-1 text-sm" value={aiSubject}
                                        onChange={(e) => { setAiSubject(e.target.value); setAiLessonId(""); }}>
                                        <option value="">All your subjects</option>
                                        {subjects.map((s) => <option key={s} value={s}>{s}</option>)}
                                    </select>
                                </label>
                                <label className="text-sm font-medium text-gray-700">
                                    Lesson <span className="text-red-500">*</span>
                                    <select required className="border border-gray-200 rounded-xl p-2.5 w-full mt-1 text-sm" value={aiLessonId}
                                        onChange={(e) => setAiLessonId(e.target.value)}>
                                        <option value="">Choose a lesson…</option>
                                        {aiLessonOptions.map((l) => (
                                            <option key={l.id} value={l.id}>
                                                {l.title}{l.topic ? ` · ${l.topic}` : ""} ({l.segmentCount || 0} sub-topics)
                                            </option>
                                        ))}
                                    </select>
                                </label>
                            </div>

                            {aiLessonOptions.length === 0 && (
                                <p className="text-xs text-gray-400">
                                    No lessons{aiSubject ? ` in ${aiSubject}` : ""} yet — create one under Lessons and add its sub-topics.
                                </p>
                            )}

                            {loadingSegments && <p className="text-sm text-gray-500">Loading sub-topics…</p>}

                            {!loadingSegments && aiLessonId && aiSegments.length === 0 && (
                                <div className="bg-amber-50 border border-amber-200 text-amber-800 text-sm rounded-lg p-3">
                                    That lesson has no sub-topics yet. Add them in the lesson builder — the AI writes one batch per sub-topic.
                                </div>
                            )}

                            {aiSegments.length > 0 && (
                                <div>
                                    <div className="flex items-center justify-between gap-2 mb-2">
                                        <p className="text-sm font-medium text-gray-700">
                                            Sub-topics <span className="text-gray-400 font-normal">· {aiSelected.size} selected</span>
                                        </p>
                                        <button type="button"
                                            onClick={() => {
                                                const readable = aiSegments.filter(isAiReadable).map((s) => s.id);
                                                setAiSelected(aiSelected.size === readable.length ? new Set() : new Set(readable));
                                            }}
                                            className="text-xs font-semibold text-blue-600 hover:underline">
                                            {aiSelected.size === aiSegments.filter(isAiReadable).length ? "Clear all" : "Select all"}
                                        </button>
                                    </div>
                                    <div className="space-y-1.5">
                                        {aiSegments.map((seg, i) => {
                                            const readable = isAiReadable(seg);
                                            return (
                                                <label key={seg.id}
                                                    title={readable ? "" : "The AI can't read this one — it needs notes or a text PDF/Word file"}
                                                    className={`flex items-center gap-3 rounded-lg border p-2.5 ${
                                                        readable ? "cursor-pointer border-gray-200 hover:border-blue-300" : "border-gray-100 bg-gray-50 cursor-not-allowed"
                                                    }`}>
                                                    <input type="checkbox" disabled={!readable}
                                                        checked={aiSelected.has(seg.id)} onChange={() => toggleSegment(seg.id)}
                                                        className="h-4 w-4 accent-blue-600 shrink-0" />
                                                    <span className="text-xs font-bold text-gray-400 w-5 shrink-0">{i + 1}</span>
                                                    <span className={`text-sm truncate flex-1 ${readable ? "text-gray-800" : "text-gray-400"}`}>
                                                        {seg.title}
                                                        {seg.topic && <span className="ml-2 text-[10px] font-medium px-1.5 py-0.5 rounded bg-blue-50 text-blue-700">{seg.topic}</span>}
                                                    </span>
                                                    {seg.questionCount > 0 && (
                                                        <span className="text-[10px] text-gray-400 shrink-0">{seg.questionCount} already</span>
                                                    )}
                                                    {seg.hasAttachment && (
                                                        <span className="text-[10px] font-semibold uppercase px-1.5 py-0.5 rounded bg-gray-100 text-gray-500 shrink-0">
                                                            {seg.attachment_kind}
                                                        </span>
                                                    )}
                                                    {!readable && (
                                                        <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 shrink-0">no text</span>
                                                    )}
                                                </label>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}

                            <div className="grid grid-cols-3 gap-3">
                                {[["mcqCount", "Multiple choice", 20], ["theoryCount", "Theory", 10], ["flashcardCount", "Flashcards", 20]].map(([key, label, max]) => (
                                    <label key={key} className="text-xs font-medium text-gray-600">
                                        {label}
                                        <input type="number" min={0} max={max} className="border border-gray-200 rounded-lg p-2 w-full mt-1 text-sm"
                                            value={aiCounts[key]} onChange={(e) => setAiCounts({ ...aiCounts, [key]: e.target.value })} />
                                        <span className="block text-[10px] text-gray-400 mt-1">per sub-topic</span>
                                    </label>
                                ))}
                            </div>

                            <div className="flex flex-wrap items-center gap-3 pt-2 border-t border-gray-100">
                                <button disabled={generating || !aiSelected.size}
                                    className="bg-blue-600 hover:bg-blue-700 text-white px-5 py-2.5 rounded-xl font-semibold flex items-center gap-2 disabled:opacity-50">
                                    <FaMagic size={13} /> {generating ? "Generating…" : `Generate for ${aiSelected.size} sub-topic${aiSelected.size === 1 ? "" : "s"}`}
                                </button>
                                <p className="text-xs text-gray-500">
                                    ≈ <span className="font-semibold text-gray-700">
                                        {((parseInt(aiCounts.mcqCount, 10) || 0) + (parseInt(aiCounts.theoryCount, 10) || 0)) * aiSelected.size}
                                    </span> questions
                                </p>
                            </div>
                        </form>
                    )}

                    {/* ---------------- My questions ---------------- */}
                    {tab === "mine" && (
                        <div className="space-y-4">
                            <div className="bg-white rounded-2xl shadow-sm p-4 sm:p-5 space-y-4">
                                <div className="flex gap-2 flex-wrap">
                                    {[["draft", "Pending review", facets.counts?.draft], ["approved", "Approved", facets.counts?.approved]].map(([value, label, count]) => (
                                        <button key={value} onClick={() => setMine({ review_status: value })}
                                            className={`px-3.5 py-1.5 rounded-full text-sm font-semibold border transition ${
                                                mineFilters.review_status === value
                                                    ? "bg-blue-600 text-white border-blue-600"
                                                    : "bg-white text-gray-600 border-gray-200 hover:border-blue-300"
                                            }`}>
                                            {label}{count != null ? ` (${count})` : ""}
                                        </button>
                                    ))}
                                </div>

                                {/* Narrow down to exactly the questions you want to look at. */}
                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                                    <label className="text-xs font-medium text-gray-600">Subject
                                        <select className="border border-gray-200 rounded-lg p-2 w-full mt-1 text-sm"
                                            value={mineFilters.subject} onChange={(e) => setMine({ subject: e.target.value })}>
                                            <option value="">All subjects</option>
                                            {facets.subjects.map((s) => <option key={s} value={s}>{s}</option>)}
                                        </select>
                                    </label>

                                    <label className="text-xs font-medium text-gray-600">Topic
                                        <select disabled={!mineFilters.subject}
                                            className="border border-gray-200 rounded-lg p-2 w-full mt-1 text-sm disabled:bg-gray-50 disabled:text-gray-400"
                                            value={mineFilters.topic} onChange={(e) => setMine({ topic: e.target.value })}>
                                            <option value="">{mineFilters.subject ? "All topics" : "Pick a subject first"}</option>
                                            {(facets.topicsBySubject[mineFilters.subject] || []).map((t) => <option key={t} value={t}>{t}</option>)}
                                        </select>
                                    </label>

                                    <label className="text-xs font-medium text-gray-600">Lesson
                                        <select className="border border-gray-200 rounded-lg p-2 w-full mt-1 text-sm"
                                            value={mineFilters.lesson_id} onChange={(e) => setMine({ lesson_id: e.target.value })}>
                                            <option value="">All lessons</option>
                                            {facets.lessons
                                                .filter((l) => !mineFilters.subject || l.subject === mineFilters.subject)
                                                .map((l) => <option key={l.id} value={l.id}>{l.title}</option>)}
                                        </select>
                                    </label>

                                    <label className="text-xs font-medium text-gray-600">Sub-topic
                                        <select disabled={!mineFilters.lesson_id}
                                            className="border border-gray-200 rounded-lg p-2 w-full mt-1 text-sm disabled:bg-gray-50 disabled:text-gray-400"
                                            value={mineFilters.segment_id} onChange={(e) => setMine({ segment_id: e.target.value })}>
                                            <option value="">{mineFilters.lesson_id ? "All sub-topics" : "Pick a lesson first"}</option>
                                            {mineSegments.map((s, i) => (
                                                <option key={s.id} value={s.id}>{i + 1}. {s.title}</option>
                                            ))}
                                            <option value="none">Not linked to a sub-topic</option>
                                        </select>
                                    </label>
                                </div>

                                <div className="flex items-center justify-between gap-3 flex-wrap">
                                    <p className="text-xs text-gray-500">
                                        {loadingMine ? "Loading…" : `${questions.length} question${questions.length === 1 ? "" : "s"}`}
                                        {activeFilterCount > 0 && ` · ${activeFilterCount} filter${activeFilterCount === 1 ? "" : "s"} on`}
                                    </p>
                                    {activeFilterCount > 0 && (
                                        <button
                                            onClick={() => setMineFilters((f) => ({ review_status: f.review_status, subject: "", topic: "", lesson_id: "", segment_id: "" }))}
                                            className="text-xs font-semibold text-blue-600 hover:underline flex items-center gap-1">
                                            <FaTimes size={10} /> Clear filters
                                        </button>
                                    )}
                                </div>
                            </div>

                            {!loadingMine && questions.length === 0 && (
                                <div className="text-center py-14 bg-white rounded-2xl shadow-sm">
                                    <div className="w-14 h-14 rounded-full bg-blue-50 text-blue-600 flex items-center justify-center mx-auto"><FaFolderOpen size={20} /></div>
                                    <p className="mt-3 text-gray-700 font-medium">No questions match</p>
                                    <p className="text-sm text-gray-400 mt-1">
                                        {activeFilterCount > 0 ? "Try clearing a filter." : `Nothing ${mineFilters.review_status === "draft" ? "pending review" : "approved"} yet.`}
                                    </p>
                                </div>
                            )}

                            <QuestionGroups
                                questions={questions}
                                topicsBySubject={topicsBySubject}
                                onApprove={approveQuestion}
                                onDelete={deleteQuestion}
                                editingQId={editingQId}
                                onEdit={setEditingQId}
                                onSaveEdit={saveQuestion}
                            />
                        </div>
                    )}
                </div>
            </div>
        </MathJaxContext>
    );
};

// Questions grouped by the sub-topic they came from, so a bank built out of
// several lessons reads as "these came from Photosynthesis → Light reactions"
// rather than one undifferentiated scroll.
const QuestionGroups = ({
    questions, topicsBySubject = {}, onApprove, onDelete, editingQId, onEdit, onSaveEdit,
}) => {
    const groups = useMemo(() => {
        const map = new Map();
        questions.forEach((q) => {
            // Key on the sub-topic when there is one, otherwise the lesson,
            // otherwise the standalone bucket.
            const key = q.segment_id ? `s${q.segment_id}` : q.lesson_id ? `l${q.lesson_id}` : "standalone";
            if (!map.has(key)) {
                map.set(key, {
                    key,
                    lesson: q.lesson_title || null,
                    subTopic: q.sub_topic_title || null,
                    items: [],
                });
            }
            map.get(key).items.push(q);
        });
        return [...map.values()];
    }, [questions]);

    if (!questions.length) return null;

    return (
        <div className="space-y-3">
            {groups.map((group) => (
                <div key={group.key} className="bg-white rounded-2xl shadow-sm p-4 sm:p-5">
                    <div className="flex items-center gap-2 mb-3 flex-wrap">
                        <FaLayerGroup className="text-blue-600 shrink-0" size={12} />
                        {group.subTopic ? (
                            <p className="text-sm font-semibold text-gray-800 flex items-center gap-1.5 flex-wrap">
                                {group.lesson && (
                                    <>
                                        <span className="text-gray-400 font-normal">{group.lesson}</span>
                                        <FaChevronRight className="text-gray-300" size={8} />
                                    </>
                                )}
                                {group.subTopic}
                            </p>
                        ) : (
                            <p className="text-sm font-semibold text-gray-800">
                                {group.lesson || "Standalone questions"}
                                {!group.lesson && <span className="text-gray-400 font-normal"> · written by hand</span>}
                            </p>
                        )}
                        <span className="text-[11px] text-gray-400 ml-auto shrink-0">{group.items.length}</span>
                    </div>

                    <div className="space-y-2.5">
                        {group.items.map((q) => (
                            <div key={q.id} className="border-t border-gray-100 pt-2.5 first:border-t-0 first:pt-0">
                                {/* The bank is browsed by topic, so that's the one taxonomy
                                    field the editor slots in; changing subject or class would
                                    move the question out of the filter it's being viewed under. */}
                                {q.id === editingQId ? (
                                    <QuestionEditor
                                        question={q}
                                        onSave={(form) => onSaveEdit(q.id, form)}
                                        onCancel={() => onEdit(null)}
                                    >
                                        {(form, set) => (
                                            <TopicSelect label="Topic" required subject={q.subject}
                                                topicsBySubject={topicsBySubject}
                                                value={form.topic} onChange={(v) => set({ topic: v })} />
                                        )}
                                    </QuestionEditor>
                                ) : (
                                <div className="flex justify-between items-start gap-4">
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-1.5 flex-wrap mb-1">
                                            <SubjectIcon subject={q.subject} size={18} iconSize={9} />
                                            <span className="text-[10px] text-gray-500">{q.subject}</span>
                                            <span className="text-gray-300">·</span>
                                            <span className="text-[10px] text-gray-500">{q.class}</span>
                                            <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-blue-50 text-blue-700">{q.topic}</span>
                                            <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-gray-100 text-gray-500">
                                                {QTYPE_LABELS[q.question_type] || q.question_type}
                                            </span>
                                            {q.source === "ai" && (
                                                <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-violet-50 text-violet-700">AI</span>
                                            )}
                                        </div>
                                        <MathText text={q.question} className="font-medium text-gray-800 text-sm" />
                                        {q.image && <img src={q.image} alt="question" className="mt-2 max-h-40 rounded-lg" />}
                                        {q.question_type === "mcq" && (
                                            <ul className="mt-1.5 text-sm text-gray-600 space-y-0.5">
                                                {["A", "B", "C", "D"].map((l) => (
                                                    <li key={l} className={q.correctAnswer === l ? "text-green-600 font-semibold" : ""}>
                                                        {l}. <MathText text={q[`option_${l.toLowerCase()}`]} />
                                                    </li>
                                                ))}
                                            </ul>
                                        )}
                                        {q.question_type === "theory" && q.explanation && (
                                            <p className="text-sm text-gray-500 mt-1 whitespace-pre-wrap">Model answer: {q.explanation}</p>
                                        )}
                                    </div>
                                    <div className="flex flex-col gap-1 shrink-0">
                                        {q.review_status === "draft" && (
                                            <button onClick={() => onApprove(q.id)} className="text-green-600 hover:bg-green-50 p-2 rounded-lg" title="Approve">
                                                <FaCheck size={13} />
                                            </button>
                                        )}
                                        <button onClick={() => onEdit(q.id)} className="text-gray-500 hover:bg-blue-50 hover:text-blue-600 p-2 rounded-lg" title="Edit">
                                            <FaPen size={13} />
                                        </button>
                                        <button onClick={() => onDelete(q.id)} className="text-red-500 hover:bg-red-50 p-2 rounded-lg" title="Delete">
                                            <FaTrash size={13} />
                                        </button>
                                    </div>
                                </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            ))}
        </div>
    );
};

export default TeacherQuestionBuilder;
